/**
 * Streaming fetch logic for useUISpec
 *
 * Extracted from use-ui-spec.ts to comply with <300 lines/file rule.
 * Handles SSE streaming mode for progressive rendering of UI specs.
 *
 * Task: NocoDB #2675 — file split
 * Agent: Diego <diego@liftping.net>
 */

import { authenticatedFetch, getEdgeFunctionUrl } from "@dramadetector/api-client";
import type { Spec } from "@json-render/core";
import type { DramaAnalysisResponse } from "@dramadetector/shared";
import { PREMIUM_COMPONENT_NAMES } from './tier-guards';
import { parseSSEStream, STREAMING_FIRST_CHUNK_TIMEOUT_MS, type UISpecTier } from "./use-ui-spec-utils";

/**
 * Premium component types filtered from partial streaming specs for non-premium users.
 * Derived from PREMIUM_COMPONENT_NAMES (tier-guards.ts) to stay in sync.
 * The server applies full tier guards on the final 'done' event via injectTierGuards().
 */
const PREMIUM_ONLY_TYPES = new Set(PREMIUM_COMPONENT_NAMES);

/**
 * Parameters needed by the streaming fetch function.
 * These are passed from the useUISpec hook closure.
 */
export interface StreamingFetchParams {
  analysisId: string;
  analysisResult: DramaAnalysisResponse;
  userTier: UISpecTier;
  abortController: AbortController;
  isCancelled: () => boolean;
  onSpec: (spec: Spec) => void;
  onPartialSpec: (spec: Spec) => void;
  onDone: (spec: Spec) => void;
  onError: (err: Error) => void;
  onStreamingStart: () => void;
  onStreamingEnd: () => void;
  onLoadingEnd: () => void;
  setReader: (reader: ReadableStreamDefaultReader<Uint8Array> | null) => void;
  clearMainTimeout: () => void;
  fetchBatchSpec: () => Promise<void>;
}

/**
 * Fetch UI spec via SSE streaming for progressive rendering.
 *
 * Falls back to batch mode if:
 * - Server returns JSON instead of SSE (flag disabled server-side)
 * - First chunk timeout fires (cold start hang, LLM stall)
 * - Any streaming error occurs
 */
export async function fetchStreamingSpec(params: StreamingFetchParams): Promise<void> {
  const {
    analysisId,
    analysisResult,
    userTier,
    abortController,
    isCancelled,
    onSpec,
    onPartialSpec,
    onDone,
    // onError intentionally unused — streaming errors fall through to batch
    // fallback. If batch also fails, the main hook's catch handles the error.
    onStreamingStart,
    onStreamingEnd,
    onLoadingEnd,
    setReader,
    clearMainTimeout,
    fetchBatchSpec,
  } = params;

  try {
    const fnUrl = getEdgeFunctionUrl('generate-ui-spec');

    const response = await authenticatedFetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        "Accept-Version": "v2",
      },
      body: JSON.stringify({
        analysisId,
        analysisResult,
      }),
      signal: abortController.signal,
    });

    if (isCancelled()) return;

    // If server didn't return SSE (e.g., flag disabled server-side),
    // it returned a normal JSON response — handle like batch mode
    const contentType = response.headers.get("Content-Type") ?? "";
    if (!contentType.includes("text/event-stream")) {
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          `generate-ui-spec error: ${(errBody as Record<string, string>)?.error || response.statusText}`
        );
      }

      const data = await response.json();

      if (isCancelled()) return;

      if (!data?.spec) {
        throw new Error("generate-ui-spec returned no spec");
      }

      // Batch response from a streaming request — set spec directly
      clearMainTimeout();
      onSpec(data.spec as Spec);
      onLoadingEnd();
      return;
    }

    // We have an SSE stream — parse it
    if (!response.body) {
      throw new Error("Streaming response has no body");
    }

    onStreamingStart();
    const reader = response.body.getReader();
    setReader(reader);

    // First-chunk timeout: if no SSE data arrives within 5s, abort
    // streaming and fall back to batch. Catches cold-start hangs and
    // LLM stalls without making users wait the full 15s.
    let firstChunkReceived = false;
    let firstChunkTimedOut = false;
    const firstChunkTimeoutId = setTimeout(() => {
      if (!firstChunkReceived && !isCancelled()) {
        firstChunkTimedOut = true;
        reader.cancel().catch(() => {});
      }
    }, STREAMING_FIRST_CHUNK_TIMEOUT_MS);

    await parseSSEStream(
      reader,
      // onPartial — progressive rendering
      (partialSpec) => {
        if (isCancelled()) return;
        // Show partial spec for progressive rendering.
        // Filter premium components from partial specs for non-premium users.
        if (partialSpec.root && partialSpec.elements) {
          if (userTier === 'free' && partialSpec.elements) {
            const safeElements = Object.fromEntries(
              Object.entries(partialSpec.elements).filter(
                ([, el]) => !PREMIUM_ONLY_TYPES.has((el as any).type)
              )
            );
            onPartialSpec({ ...partialSpec, elements: safeElements } as Spec);
          } else {
            onPartialSpec(partialSpec as Spec);
          }
          onLoadingEnd(); // Content is visible, no longer "loading"
        }
      },
      // onDone — final tier-guarded spec from server
      (finalSpec) => {
        if (isCancelled()) return;
        clearTimeout(firstChunkTimeoutId);
        clearMainTimeout();
        onDone(finalSpec);
      },
      // onError — streaming error, fall back to batch
      (errorMsg) => {
        if (isCancelled()) return;
        clearTimeout(firstChunkTimeoutId);
        throw new Error(`Streaming error: ${errorMsg}`);
      },
      // onRawData — any bytes received, clear first-chunk timeout
      () => {
        if (!firstChunkReceived) {
          firstChunkReceived = true;
          clearTimeout(firstChunkTimeoutId);
        }
      },
    );

    // If parseSSEStream exited because the first-chunk timeout cancelled the
    // reader (done=true without any data), throw to trigger batch fallback.
    if (firstChunkTimedOut) {
      throw new Error(
        `Streaming first-chunk timeout: no data within ${STREAMING_FIRST_CHUNK_TIMEOUT_MS / 1000}s`
      );
    }
  } catch (err) {
    if (isCancelled()) return;

    // Streaming failed — fall back to batch mode
    onStreamingEnd();
    setReader(null);

    // Delegate to batch fallback
    await fetchBatchSpec();
  }
}
